import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../services/supabase';
import SeoHead from '../components/seo/SeoHead';
import './Signup.css';

const STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry'
];

const WAVE_BARS = 28;

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: '',
    role: '' as 'producer' | 'rapper' | 'producer_rapper' | 'listener' | '',
    state: '',
    instagram: '',
    youtube: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(t);
  }, []);

  // Function to check if username or email already exists
  const checkExistingUser = async (email: string, username: string) => {
    try {
      // Check if username exists in profiles table
      const { data: existingProfiles, error: profileError } = await supabase
        .from('profiles')
        .select('username')
        .eq('username', username);

      if (profileError) {
        console.error('Error checking profiles:', profileError);
        return { emailExists: false, usernameExists: false };
      }

      const usernameExists = existingProfiles && existingProfiles.length > 0;

      // For email validation, we'll rely on the signup process itself
      // and handle the error appropriately
      return { emailExists: false, usernameExists };
    } catch (error) {
      console.error('Error checking existing users:', error);
      return { emailExists: false, usernameExists: false };
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (!formData.role) {
      setError('Please select a role');
      setLoading(false);
      return;
    }

    if (formData.role !== 'listener' && !formData.state) {
      setError('Please select your state');
      setLoading(false);
      return;
    }

    try {
      // Check if username already exists
      const { usernameExists } = await checkExistingUser(formData.email, formData.username);
      
      if (usernameExists) {
        setError('This username is already taken. Please choose a different username.');
        setLoading(false);
        return;
      }

      // Log the metadata being sent to Supabase
      console.log('Signup metadata:', formData);
      
      // Attempt to create the auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({ 
        email: formData.email, 
        password: formData.password,
        options: {
          data: {
            username: formData.username,
            role: formData.role,
            state: formData.state,
            instagram: formData.instagram,
            youtube: formData.youtube
          }
        }
      });
      
      if (authError) {
        // Handle specific Supabase auth errors
        if (authError.message && (
          authError.message.toLowerCase().includes('already registered') ||
          authError.message.toLowerCase().includes('user already registered') ||
          authError.message.toLowerCase().includes('email already in use')
        )) {
          setError('An account with this email already exists. Please try a different email or sign in to your existing account.');
        } else {
          setError(authError.message || 'Error creating account. Please try again.');
        }
        setLoading(false);
        return;
      }
      
      // Check if the signup was successful but user already exists
      if (authData.user && authData.user.identities && authData.user.identities.length === 0) {
        setError('An account with this email already exists. Please try a different email or sign in to your existing account.');
        setLoading(false);
        return;
      }
      
      if (!authData.user) {
        throw new Error('Failed to create user account');
      }

      // For new signups, session will be null because email confirmation is required
      // We only need to check if the user was actually created
      if (authData.user && !authData.session) {
        // This is a successful new signup that requires email confirmation
        console.log('New user created, email confirmation required');
      }

      // No need to create profile here; it will be created after activation

      // Only navigate to activate page after successful signup
      navigate('/activate', { replace: true });
    } catch (err) {
      console.error('Signup error:', err);
      setError('Error creating account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`signup-page ${visible ? 'signup-page--visible' : ''}`}>
      <SeoHead
        title="Sign Up"
        description="Join DHH Collab free — the Desi Hip-Hop (DHH) platform for producers, rappers, and listeners in India. Upload beats, record verses, and collaborate online."
        path="/signup"
        keywords="DHH signup, join Desi Hip Hop, Indian hip hop platform, DHH Collab register, rap collaboration India"
      />

      <div className="signup-page__bg" aria-hidden>
        <span className="signup-page__blob signup-page__blob--1" />
        <span className="signup-page__blob signup-page__blob--2" />
        <span className="signup-page__grid" />
      </div>

      <div className="signup-page__inner container-padding">
        <div className="signup-page__layout">
          <aside className="signup-page__visual" aria-hidden>
            <span className="signup-page__eyebrow">Join the Movement</span>
            <h1 className="signup-page__title">Join DHH Collab</h1>
            <p className="signup-page__lead">
              Producers, rappers, and listeners — one platform to upload beats, drop verses, and build India&apos;s hip-hop scene together.
            </p>

            <div className="signup-page__graphic">
              <div className="signup-page__network">
                <div className="signup-page__network-core">
                  <span className="signup-page__network-line signup-page__network-line--1" />
                  <span className="signup-page__network-line signup-page__network-line--2" />
                  <span className="signup-page__network-line signup-page__network-line--3" />
                  <div className="signup-page__vinyl" />
                  <div className="signup-page__node signup-page__node--producer">
                    <span className="signup-page__node-icon" aria-hidden>🎹</span>
                    <span className="signup-page__node-label">Producer</span>
                  </div>
                  <div className="signup-page__node signup-page__node--rapper">
                    <span className="signup-page__node-icon" aria-hidden>🎤</span>
                    <span className="signup-page__node-label">Rapper</span>
                  </div>
                  <div className="signup-page__node signup-page__node--listener">
                    <span className="signup-page__node-icon" aria-hidden>🎧</span>
                    <span className="signup-page__node-label">Listener</span>
                  </div>
                </div>
              </div>
              <div className="signup-page__wave">
                {Array.from({ length: WAVE_BARS }).map((_, i) => {
                  const h = 20 + Math.abs(Math.sin(i * 0.6) * 65) + (i % 3) * 5;
                  return (
                    <span
                      key={i}
                      className="signup-page__wave-bar"
                      style={
                        {
                          '--bar-i': i,
                          '--bar-height': `${h}%`,
                        } as React.CSSProperties
                      }
                    />
                  );
                })}
              </div>
            </div>
          </aside>

          <div className="signup-page__card">
            <header className="signup-page__card-head">
              <h2 className="signup-page__card-title">Create your account</h2>
              <p className="signup-page__card-subtitle">Start collaborating in minutes</p>
            </header>

            <form className="signup-page__form" onSubmit={handleSubmit}>
              <div className="signup-page__fields">
                <div className="signup-page__field">
                  <label htmlFor="email" className="signup-page__label">
                    Email address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="signup-page__input"
                    placeholder="Enter your email"
                  />
                </div>

                <div className="signup-page__field">
                  <label htmlFor="password" className="signup-page__label">
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="signup-page__input"
                    placeholder="Create a password"
                  />
                </div>

                <div className="signup-page__field">
                  <label htmlFor="username" className="signup-page__label">
                    Username
                  </label>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    required
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="signup-page__input"
                    placeholder="Choose a username"
                  />
                </div>

                <div className="signup-page__field">
                  <label htmlFor="role" className="signup-page__label">
                    I am a
                  </label>
                  <select
                    id="role"
                    name="role"
                    required
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                    className="signup-page__select"
                  >
                    <option value="">Select your role</option>
                    <option value="producer">Producer</option>
                    <option value="rapper">Rapper</option>
                    <option value="producer_rapper">Producer & Rapper</option>
                    <option value="listener">Listener</option>
                  </select>
                </div>

                {formData.role !== 'listener' && (
                  <>
                    <div className="signup-page__field">
                      <label htmlFor="state" className="signup-page__label">
                        State
                      </label>
                      <select
                        id="state"
                        name="state"
                        required
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        className="signup-page__select"
                      >
                        <option value="">Select your state</option>
                        {STATES.map((state) => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>

                    <div className="signup-page__field">
                      <label htmlFor="instagram" className="signup-page__label">
                        Instagram Handle (optional)
                      </label>
                      <input
                        id="instagram"
                        name="instagram"
                        type="text"
                        value={formData.instagram}
                        onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                        className="signup-page__input"
                        placeholder="@username or username"
                      />
                    </div>

                    <div className="signup-page__field">
                      <label htmlFor="youtube" className="signup-page__label">
                        YouTube Channel (optional)
                      </label>
                      <input
                        id="youtube"
                        name="youtube"
                        type="text"
                        value={formData.youtube}
                        onChange={(e) => setFormData({ ...formData, youtube: e.target.value })}
                        className="signup-page__input"
                        placeholder="Channel URL"
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="signup-page__disclaimer">
                <strong>Need help?</strong> If you encounter any issues during signup or have questions, please contact our support team through the{' '}
                <Link to="/contact">Contact Us</Link>{' '}
                page.
              </div>

              {error && <div className="signup-page__error">{error}</div>}

              <button
                type="submit"
                disabled={loading}
                className={`signup-page__submit ${loading ? 'signup-page__submit--disabled' : 'signup-page__submit--ready'}`}
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </button>

              <p className="signup-page__login">
                Already have an account? <Link to="/login">Sign in</Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
